import { Alert, Grid, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  BlueButton,
  RootineMultiSelect,
  RootineRadioButtons,
} from '../../atoms';
import { APIRequests } from '../../config/api';
import {
  Subscription,
  SubscriptionRequestType,
  UserSubscriptionRequest,
} from '../../types';
import useAPI from '../../utils/useApi';
import { Loading } from '../Layout';

const cancellationReasons = [
  "It's too expensive",
  "I can't afford it right now",
  "It's not working for me",
  "I'm not sure if it's working",
  'Shipment issues with testing',
  'Shipment issues with the multivitamin',
  "I'd like to pause or skip my next order",
  'Other (please explain)',
];

const pauseOptions = [
  {
    label: '1 month',
    value: 1,
  },
  {
    label: '2 months',
    value: 2,
  },
  {
    label: '3 months',
    value: 3,
  },
];

export interface ManageSubscriptionProps {
  subscription: Subscription;
  onSuccess: () => void;
}

export const ManageSubscription = ({
  subscription,
  onSuccess,
}: ManageSubscriptionProps): JSX.Element => {
  const [reason, setReason] = useState(cancellationReasons[0]);
  const [detail, setDetail] = useState('');
  const [pauseLength, setPauseLength] = useState(1);
  const [requestType, setRequestType] =
    useState<SubscriptionRequestType | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  let subManageReq = useAPI<Subscription>(APIRequests.UserSubscriptionsManage);
  const manageSubscription = (body: UserSubscriptionRequest) => {
    return subManageReq.execute({ body });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!reason || !detail) {
      setError('Please complete the form');
      return;
    }
    setLoading(true);
    manageSubscription({
      subscription_id: subscription.id,
      type: requestType!,
      reason,
      detail,
      pause_length:
        requestType === SubscriptionRequestType.pause ? pauseLength : 0,
    })
      .then(() => {
        setSuccess(true);
        resetState();
      })
      .catch((e) => {
        setLoading(false);
        setError('Error processing request');
      });
  };

  const resetState = () => {
    setLoading(false);
    setReason(cancellationReasons[0]);
    setDetail('');
    setError('');
    setPauseLength(1);
    setRequestType(null);
  };

  const pauseOrCancel = () => (
    <Grid container item xs={12} rowSpacing={2}>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 16, md: 18 } }}
        >{`Did you actually want to pause your subscription?`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ fontSize: { xs: 12, md: 14 } }}>
          {`Pausing your subscription is a convenient way to delay your next shipment, 
        in case you're traveling or just need more time with what you've currently got.`}
        </Typography>
      </Grid>
      <Grid container item xs={12} justifyContent="center" alignItems="center">
        <Grid item xs={6}>
          <BlueButton
            fontSize={12}
            label="Pause"
            buttonfill="true"
            onClick={() => setRequestType(SubscriptionRequestType.pause)}
          />
        </Grid>
        <Grid item xs={6}>
          <BlueButton
            fontSize={12}
            label={`Continue to cancel`}
            onClick={() => setRequestType(SubscriptionRequestType.cancel)}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  const feedbackAndSubmit = () => (
    <Grid container item xs={12} rowSpacing={2}>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 16, md: 18 } }}
        >{`We'd love your feedback`}</Typography>
      </Grid>
      <Grid container alignItems="center">
        {error && (
          <Grid item>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 12, md: 14 } }}
        >{`Please select your reason`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <RootineMultiSelect
          options={cancellationReasons.map((reason, i) => ({
            key: i,
            value: reason,
          }))}
          onChange={(select) => {
            setReason(select.value);
          }}
        />
      </Grid>
      {requestType === SubscriptionRequestType.pause && (
        <>
          <Grid item xs={12}>
            <Typography
              sx={{ fontSize: { xs: 12, md: 14 } }}
            >{`How long would you like to pause?`}</Typography>
          </Grid>
          <Grid item xs={12}>
            <RootineRadioButtons
              options={pauseOptions}
              onChange={(e) => setPauseLength(+e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 12, md: 14 } }}
        >{`Add more details so we can better assist you`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={detail}
          fullWidth
          multiline
          rows={4}
          size="small"
          onChange={(e: any) => setDetail(e.target.value)}
        />
      </Grid>

      <Grid item xs={6}>
        <BlueButton
          fontSize={12}
          label={`Wait, go back!`}
          buttonfill="true"
          onClick={() => resetState()}
        />
      </Grid>
      <Grid item xs={6}>
        {loading ? (
          <Loading />
        ) : (
          <BlueButton
            fontSize={12}
            label={`Submit`}
            onClick={(e: any) => handleSubmit(e)}
          />
        )}
      </Grid>
    </Grid>
  );

  const successDetail = () => (
    <Grid container item xs={12} rowSpacing={2}>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 16, md: 18 } }}
        >{`Your request is now being processed.`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 16, md: 18 } }}
        >{`Our team will follow-up soon (usually within one business day and often, same-day).`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: { xs: 16, md: 18 } }}
        >{`If you'd like to reach out to our team directly, please open the chat below.`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <BlueButton
          fontSize={12}
          label={`Done`}
          buttonfill="true"
          onClick={() => onSuccess()}
        />
      </Grid>
    </Grid>
  );

  return (
    <Grid container item xs={12} pt={2}>
      {!requestType && !success
        ? pauseOrCancel()
        : !success
        ? feedbackAndSubmit()
        : successDetail()}
    </Grid>
  );
};
